"use client"

import type { QuestionType, Resume } from "@/modules/events/types/eventDetailsApi"
import type { Dispatch, FunctionComponent, PropsWithChildren, SetStateAction } from "react"

import { createContext, useContext, useMemo, useState } from "react"

export type RegistrationFormAnswerType = string | string[] | File | null

export type RegistrationFormQuestionType = {
  id: string
  answer: RegistrationFormAnswerType
  type: QuestionType
  error: string
}

type RegistrationFormFileType = Resume | null

type RegistrationFormResumeType = {
  file: RegistrationFormFileType
  error: string
}

type RegistrationFormSubeventType = {
  id: string
  isRegistered: boolean
}

export type RegistrationFormDataType = {
  questions: RegistrationFormQuestionType[]
  profileResumeId: string | null
  tailoredResume: RegistrationFormResumeType
  tailoredResumeId: string | null
  resumeType: "profile_resume" | "tailored_resume" | null
  subEvents: {
    values: RegistrationFormSubeventType[]
    error: string
  }
}

export type RegistrationFormContextType = {
  isNewSignInFlowEnabled: boolean
  registrationFormData: RegistrationFormDataType
  setRegistrationFormData: Dispatch<SetStateAction<RegistrationFormDataType>>
}

const RegistrationFormContext = createContext<RegistrationFormContextType | undefined>(undefined)

export const useRegistrationForm = (): RegistrationFormContextType => {
  const context = useContext(RegistrationFormContext)
  if (context === undefined) {
    throw new Error("useRegistrationForm must be used within a RegistrationFormProvider")
  }
  return context
}

type RegistrationFormProviderProps = PropsWithChildren<{
  isNewSignInFlowEnabled?: boolean
}>

export const RegistrationFormProvider: FunctionComponent<RegistrationFormProviderProps> = ({
  children,
  isNewSignInFlowEnabled = false,
}) => {
  const [registrationFormData, setRegistrationFormData] = useState<RegistrationFormDataType>({
    profileResumeId: null,
    questions: [],
    resumeType: null,
    subEvents: {
      error: "",
      values: [],
    },
    tailoredResume: {
      error: "",
      file: null,
    },
    tailoredResumeId: null,
  })

  const value = useMemo(
    () => ({
      isNewSignInFlowEnabled,
      registrationFormData,
      setRegistrationFormData,
    }),
    [isNewSignInFlowEnabled, registrationFormData, setRegistrationFormData]
  )

  return <RegistrationFormContext.Provider value={value}>{children}</RegistrationFormContext.Provider>
}
